import { FC, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { Button, Icon } from '@factorialco/factorial-one-react'
import { Delete, Handle } from '@factorialco/factorial-one-react/icons/app'

import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { ElementOption } from 'modules/forms/lib/types'

interface DragItem {
  fromIndex: number
  toIndex: number
}

type Props = {
  handleUpdateOption: (index: number, label: string) => void
  handleRemoveOption: (index: number) => void
  option: ElementOption
  index: number
  restartInitialValues: () => void
  moveElement?: (fromIndex: number, toIndex: number) => void
  onDrop?: (fromIndex: number, toIndex: number) => void
}

export const ChoiceElement: FC<Props> = ({
  handleUpdateOption,
  option,
  handleRemoveOption,
  index,
  restartInitialValues,
  moveElement,
  onDrop,
}) => {
  const [isHovering, toggleHovering] = useToggle(false)

  const [{ isDragging }, drag] = useDrag(
    {
      type: 'Option',
      item: (): DragItem => ({
        fromIndex: index,
        toIndex: index,
      }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (_item, monitor) => {
        const didDrop = monitor.didDrop()
        if (!didDrop) {
          restartInitialValues()
        }
      },
    },
    [option]
  )
  const [, drop] = useDrop(
    {
      accept: 'Option',
      hover(item: DragItem) {
        if (!moveElement || item.toIndex === index) return
        moveElement(item.toIndex, index)
        item.toIndex = index
      },
      drop(item: DragItem) {
        if (!onDrop) return
        onDrop(item.fromIndex, item.toIndex)
      },
    },
    [option]
  )
  const [optionLabel, setOptionLabel] = useState<string>(option.label)
  return (
    <Box
      ref={(node) => drag(drop(node))}
      gap="s8"
      flexDirection="row"
      alignItems="center"
      padding="s8"
      cursor="pointer"
      borderRadius={{
        all: 'abs012',
      }}
      background={isHovering ? 'grey200' : 'transparent'}
      onMouseEnter={toggleHovering(true)}
      onMouseLeave={toggleHovering(false)}
      style={{
        opacity: isDragging ? '0.4' : '1',
        transition: 'opacity 0.2s ease, background-color 0.2s ease',
      }}
    >
      <Box>
        <Icon size="xs" icon={Handle} />
      </Box>
      <TextField
        hideBorder
        value={optionLabel}
        onChange={setOptionLabel}
        onBlur={() => {
          if (optionLabel == option.label) return
          handleUpdateOption(index, optionLabel)
        }}
        placeholder={i18n.t('forms.editor.options.placeholder')}
      />
      <Button
        icon={Delete}
        hideLabel
        variant="ghost"
        aria-label={i18n.t('forms.editor.options.placeholder')}
        onClick={() => handleRemoveOption(index)}
      />
    </Box>
  )
}
